import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toast", "unreadCount"]
  static values = {
    type: String,
    autoHide: { type: Boolean, default: true },
    hideDelay: { type: Number, default: 5000 }
  }

  connect() {
    if (this.autoHideValue && this.hasToastTarget) {
      this.hideTimer = setTimeout(() => this.hideToast(), this.hideDelayValue)
    }

    if (this.element.classList.contains('notification-flash')) {
      this.autoHideFlash()
    }

    // Always ensure notification panel is closed on controller connect
    // Prevents stale Turbo cache from restoring an open panel
    const sheet = document.getElementById('nc-sheet')
    this.closeRail(sheet)
    this.updateTriggerState(false)
    document.body.classList.remove('nc-rail-open')

    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    this.boundOnKeydown = this.onKeydown.bind(this)

    // Clean up notification panel state on Turbo navigation
    // Prevents the panel from appearing open when navigating back or forward
    this.boundBeforeCache = () => this.closeModal()
    this.boundBeforeRender = () => this.closeModal()
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    document.addEventListener("turbo:before-render", this.boundBeforeRender)
    document.addEventListener("turbo:visit", this.boundBeforeCache)
  }

  disconnect() {
    if (this.hideTimer) clearTimeout(this.hideTimer)
    if (this.emptyPingTimer) clearTimeout(this.emptyPingTimer)
    this.dismissEmptyPing()
    document.removeEventListener("click", this.boundCloseOnClickOutside)
    document.removeEventListener("keydown", this.boundOnKeydown)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    document.removeEventListener("turbo:before-render", this.boundBeforeRender)
    document.removeEventListener("turbo:visit", this.boundBeforeCache)
  }

  // ─── Flash / Toast (unchanged) ───

  autoHideFlash() {
    setTimeout(() => {
      this.element.style.transition = "all 0.3s ease-out"
      this.element.style.opacity = "0"
      this.element.style.transform = "translateX(100%)"
      setTimeout(() => this.element.remove(), 300)
    }, 3000)
  }

  showToast(event) {
    const { title, message, type = 'info', autoHide = true, hideDelay = 5000 } = event.detail
    const toast = this.createToast(title, message, type)
    this.addToastToContainer(toast)
    setTimeout(() => {
      toast.classList.remove('translate-x-full', 'opacity-0')
      toast.classList.add('translate-x-0', 'opacity-100')
    }, 100)
    if (autoHide) setTimeout(() => this.removeToast(toast), hideDelay)
  }

  hideToast(event) {
    if (event) event.preventDefault()
    if (this.hasToastTarget) {
      this.toastTarget.classList.add('translate-x-full', 'opacity-0')
      setTimeout(() => { if (this.toastTarget.parentNode) this.toastTarget.parentNode.removeChild(this.toastTarget) }, 300)
    }
  }

  // ─── Notification Center Modal ───

  toggleDropdown(event) {
    event.preventDefault()
    event.stopPropagation()

    const sheet = document.getElementById('nc-sheet')
    if (!sheet) return

    if (this.shouldUseDedicatedMobileList()) {
      window.location.href = event.currentTarget.dataset.notificationMobilePath || sheet.dataset.mobileDestination || '/notifications'
      return
    }

    if (sheet.classList.contains('nc-open')) {
      this.closeModal()
    } else {
      this.openModal()
    }
  }

  openModal() {
    const sheet = document.getElementById('nc-sheet')
    if (!sheet) return

    if (sheet.dataset.emptyState === 'true') {
      this.closeModal()
      this.showEmptyPing()
      return
    }

    document.body.classList.add('nc-rail-open')
    sheet.classList.add('nc-open')
    sheet.setAttribute('aria-hidden', 'false')
    this.updateTriggerState(true)

    document.addEventListener("keydown", this.boundOnKeydown)
    document.addEventListener("click", this.boundCloseOnClickOutside)

    // Reset to "All" tab
    this.activeTab = 'all'
    this.updateTabs()
  }

  closeModal() {
    const sheet = document.getElementById('nc-sheet')
    if (!sheet) return

    this.closeRail(sheet)
    this.updateTriggerState(false)
    document.body.classList.remove('nc-rail-open')
    document.removeEventListener("keydown", this.boundOnKeydown)
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  closeOverlay(event) {
    this.closeModal()
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeModal()
  }

  closeOnClickOutside(event) {
    const sheet = document.getElementById('nc-sheet')
    const trigger = event.target.closest('[data-action*="notification#toggleDropdown"]')
    if (trigger) return

    if (sheet?.classList.contains('nc-open') && !sheet.contains(event.target)) {
      this.closeModal()
    }
  }

  // ─── Tab Filtering ───

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this.activeTab = tab
    this.updateTabs()
  }

  updateTabs() {
    const tab = this.activeTab || 'all'

    // Update tab buttons
    document.querySelectorAll('.nc-tab').forEach(btn => {
      btn.classList.toggle('nc-tab-active', btn.dataset.tab === tab)
      btn.setAttribute('aria-selected', btn.dataset.tab === tab ? 'true' : 'false')
    })

    // Filter cards
    document.querySelectorAll('#nc-notifications-body .nc-card').forEach(card => {
      if (tab === 'all') {
        card.style.display = ''
      } else {
        card.style.display = card.dataset.unread === 'true' ? '' : 'none'
      }
    })

    // Show/hide group labels based on visible children
    document.querySelectorAll('#nc-notifications-body .nc-time-group').forEach(group => {
      const visibleCards = group.querySelectorAll('.nc-card:not([style*="display: none"])')
      const label = group.querySelector('.nc-group-label')
      if (label) label.style.display = visibleCards.length ? '' : 'none'
      // If no cards at all, hide the entire group
      group.style.display = visibleCards.length ? '' : 'none'
    })

    // Toggle empty state
    const allVisible = document.querySelectorAll('#nc-notifications-body .nc-card:not([style*="display: none"])')
    const emptyAll = document.getElementById('nc-empty-all')
    const emptyUnread = document.getElementById('nc-empty-unread')
    if (emptyAll) emptyAll.style.display = (tab === 'all' && allVisible.length === 0) ? '' : 'none'
    if (emptyUnread) emptyUnread.style.display = (tab === 'unread' && allVisible.length === 0) ? '' : 'none'
  }

  // ─── Mark as Read ───

  async markAsRead(event) {
    event.preventDefault()

    const card = event.currentTarget.closest('.nc-card') || event.currentTarget
    const notificationId = card.dataset.notificationId
    const targetUrl = card.dataset.notificationUrl

    if (!notificationId) return

    // If already read, just navigate
    if (card.dataset.unread !== 'true') {
      if (targetUrl) {
        this.closeModal()
        window.location.href = targetUrl
      }
      return
    }

    // Visual feedback
    card.classList.add('nc-marking-read')
    card.classList.remove('nc-card-unread')
    card.dataset.unread = 'false'
    const dot = card.querySelector('.nc-unread-dot')
    if (dot) dot.remove()

    try {
      const response = await fetch(`/notifications/${notificationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ intent: 'mark_read_only' })
      })

      if (response.ok) {
        const data = await response.json()
        this.refreshUnreadBadge(data.unread_count)
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }

    // If unread tab is active, re-filter
    if (this.activeTab === 'unread') {
      this.updateTabs()
    }

    // Navigate after a beat
    if (targetUrl) {
      setTimeout(() => {
        this.closeModal()
        window.location.href = targetUrl
      }, 300)
    }
  }

  async markAllAsRead(event) {
    event.preventDefault()

    // Immediate visual update
    document.querySelectorAll('#nc-notifications-body .nc-card-unread').forEach(card => {
      card.classList.remove('nc-card-unread')
      card.dataset.unread = 'false'
      const dot = card.querySelector('.nc-unread-dot')
      if (dot) dot.remove()
    })

    this.refreshUnreadBadge(0)

    try {
      await fetch('/notifications/mark_all_as_read', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }

    if (this.activeTab === 'unread') {
      this.updateTabs()
    }
  }

  refreshUnreadBadge(count) {
    // Update all badge instances (navbar + modal)
    document.querySelectorAll('[data-notification-target="unreadCount"]').forEach(el => {
      if (count > 0) {
        el.textContent = count > 9 ? '9+' : count
        el.classList.remove('hidden')
      } else {
        el.classList.add('hidden')
      }
    })
    const modalBadge = document.getElementById('nc-unread-badge')
    if (modalBadge) {
      modalBadge.textContent = count
      modalBadge.style.display = count > 0 ? '' : 'none'
    }
    // Hide mark all button when 0
    const markAllBtn = document.getElementById('nc-mark-all-btn')
    if (markAllBtn) markAllBtn.style.display = count > 0 ? '' : 'none'
  }

  updateTriggerState(isOpen) {
    document.querySelectorAll('[aria-controls="nc-sheet"]').forEach(trigger => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
    })
  }

  shouldUseDedicatedMobileList() {
    return window.matchMedia('(max-width: 767px)').matches
  }

  closeRail(sheet = document.getElementById('nc-sheet')) {
    if (!sheet) return
    sheet.classList.remove('nc-open')
    sheet.setAttribute('aria-hidden', 'true')
  }

  showEmptyPing() {
    const existingPing = document.getElementById('nc-empty-ping')
    if (existingPing) existingPing.remove()

    const trigger = document.querySelector('[aria-controls="nc-sheet"]')
    const ping = document.createElement('div')
    ping.id = 'nc-empty-ping'
    ping.className = 'nc-empty-ping'
    ping.setAttribute('role', 'status')
    ping.innerHTML = `
      <span class="nc-empty-ping-icon"><i class="fas fa-bell-slash"></i></span>
      <span>
        <strong>All caught up</strong>
        <small>No notifications.</small>
      </span>
    `

    document.body.appendChild(ping)

    if (trigger) {
      const rect = trigger.getBoundingClientRect()
      const pingRect = ping.getBoundingClientRect()
      const top = Math.min(window.innerHeight - pingRect.height - 16, rect.bottom + 10)
      const left = Math.min(
        window.innerWidth - pingRect.width - 12,
        Math.max(12, rect.right - pingRect.width)
      )
      ping.style.top = `${Math.max(12, top)}px`
      ping.style.left = `${left}px`
    }

    requestAnimationFrame(() => ping.classList.add('nc-visible'))
    clearTimeout(this.emptyPingTimer)
    this.emptyPingTimer = setTimeout(() => this.dismissEmptyPing(ping), 1800)
  }

  dismissEmptyPing(ping = document.getElementById('nc-empty-ping')) {
    if (!ping) return
    ping.classList.remove('nc-visible')
    setTimeout(() => {
      if (ping.parentNode) ping.remove()
    }, 180)
  }

  // Legacy close (for old dropdown references)
  closeDropdown() {
    this.closeModal()
  }

  // ─── Unread Count ───

  updateUnreadCount(event) {
    const count = event.detail.count || 0
    this.refreshUnreadBadge(count)
  }

  // ─── Toast Helpers (unchanged) ───

  createToast(title, message, type) {
    const toast = document.createElement('div')
    toast.className = 'transform translate-x-full opacity-0 transition-all duration-300 ease-out max-w-sm w-full bg-white dark:bg-night-surface shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 dark:ring-night-border'
    const iconClass = this.getIconClass(type)
    const iconColor = this.getIconColor(type)
    toast.innerHTML = `
      <div class="flex-1 w-0 p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0"><i class="${iconClass} ${iconColor} text-xl"></i></div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm font-medium text-gray-900 dark:text-night-text-primary">${title}</p>
            <p class="mt-1 text-sm text-gray-500 dark:text-night-text-secondary">${message}</p>
          </div>
        </div>
      </div>
      <div class="flex border-l border-gray-200 dark:border-night-border">
        <button onclick="this.parentElement.parentElement.remove()"
                class="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-night-text-secondary hover:text-gray-500 focus:outline-none">
          <i class="fas fa-times"></i>
        </button>
      </div>`
    return toast
  }

  getToastContainer() {
    let container = document.getElementById('toast-container')
    if (!container) {
      container = document.createElement('div')
      container.id = 'toast-container'
      container.className = 'fixed top-0 right-0 flex flex-col items-end space-y-4 sm:top-6 sm:right-6 p-4 sm:p-0 max-w-sm w-full pointer-events-none'
      container.style.zIndex = '9999999'
      document.body.appendChild(container)
    }
    return container
  }

  addToastToContainer(toast) { this.getToastContainer().appendChild(toast) }

  removeToast(toast) {
    toast.classList.add('translate-x-full', 'opacity-0')
    setTimeout(() => { if (toast.parentNode) toast.parentNode.removeChild(toast) }, 300)
  }

  getIconClass(type) {
    const map = { success: 'fa-check-circle', error: 'fa-exclamation-circle', danger: 'fa-exclamation-circle', warning: 'fa-exclamation-triangle', comment: 'fa-comment', reply: 'fa-reply', like: 'fa-heart', follow: 'fa-user-plus', post: 'fa-plus', quote: 'fa-file-invoice-dollar', lead: 'fa-user-tag', service: 'fa-wrench', progress: 'fa-tasks', review: 'fa-star', message: 'fa-comment-dots', delivery: 'fa-truck' }
    return `fas ${map[type] || 'fa-info-circle'}`
  }

  getIconColor(type) {
    const map = { success: 'text-green-400', error: 'text-red-400', danger: 'text-red-400', warning: 'text-yellow-400', comment: 'text-blue-400', reply: 'text-blue-400', like: 'text-red-400', follow: 'text-purple-400', post: 'text-yellow-400', quote: 'text-emerald-400', lead: 'text-orange-400', service: 'text-blue-400', progress: 'text-purple-400', review: 'text-yellow-400', message: 'text-indigo-400', delivery: 'text-teal-400' }
    return map[type] || 'text-gray-400'
  }
}
